import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { WarningCircle, TrashSimple, Info, Power } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Three-block section on the Profile Settings sidebar's "Account" tab.
 *
 * Block A — Temporary deactivation (self-service suspend, no `status_auto`,
 *          so it's safe from the lifecycle auto-delete sweep).
 * Block B — Permanent deletion (password + acknowledgement gated,
 *          triggers backend anonymization + confirmation email).
 * Block C — Read-only inactivity policy explainer (no side-effects).
 *
 * All copy passes through i18n; no user-visible string is hardcoded.
 */
export const AccountManagementSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const clearAuth = auth.logout || auth.clearAuth || (() => {});

  // Block A state
  const [deactModalOpen, setDeactModalOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Block B state
  const [password, setPassword] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [delModalOpen, setDelModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDeactivate = async () => {
    setDeactivating(true);
    try {
      await axios.post(`${API}/auth/deactivate`, {}, { withCredentials: true });
      toast.success(t('profileSettings.account.deactivate.success'));
      setDeactModalOpen(false);
      // Log the user out client-side; on reconnect the backend flips
      // status back to active automatically.
      try { clearAuth(); } catch { /* no-op */ }
      navigate('/');
    } catch {
      toast.error(t('common.error') || 'Error');
    } finally { setDeactivating(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { password, understood: true },
        { withCredentials: true },
      );
      toast.success(t('profileSettings.account.delete.success'));
      setDelModalOpen(false);
      try { clearAuth(); } catch { /* no-op */ }
      // Delay slightly so the toast is visible before nav.
      setTimeout(() => navigate('/'), 800);
    } catch (e) {
      const msg = e?.response?.status === 401
        ? t('profileSettings.account.delete.error')
        : (e?.response?.data?.detail || t('common.error') || 'Error');
      toast.error(msg);
    } finally { setDeleting(false); }
  };

  const canConfirmDelete = password.length > 0 && understood;

  return (
    <div className="space-y-6" data-testid="account-management-section">
      {/* Block A — Temporary deactivation */}
      <div className="bg-white border border-warning/40 rounded-2xl p-6" data-testid="account-deactivate-block">
        <h2 className="font-semibold font-heading text-warning-strong mb-2 flex items-center gap-2">
          <Power size={20} weight="fill" />
          {t('profileSettings.account.deactivate.title')}
        </h2>
        <p className="text-sm text-text_muted mb-4">
          {t('profileSettings.account.deactivate.description')}
        </p>
        <Button
          variant="danger"
          onClick={() => setDeactModalOpen(true)}
          data-testid="account-deactivate-btn"
        >
          <Power size={16} className="mr-2" />
          {t('profileSettings.account.deactivate.button')}
        </Button>
      </div>

      {/* Block B — Permanent deletion */}
      <div className="bg-white border border-danger/40 rounded-2xl p-6" data-testid="account-delete-block">
        <h2 className="font-semibold font-heading text-danger mb-2 flex items-center gap-2">
          <WarningCircle size={20} weight="fill" />
          {t('profileSettings.account.delete.title')}
        </h2>
        <p className="text-sm text-text_muted mb-4">
          {t('profileSettings.account.delete.description')}
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="delete-password">{t('profileSettings.account.delete.passwordLabel')}</Label>
            <Input
              id="delete-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('profileSettings.account.delete.passwordPlaceholder')}
              className="border border-border"
              data-testid="account-delete-password"
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              className="mt-1"
              data-testid="account-delete-understood"
            />
            <span>{t('profileSettings.account.delete.understandLabel')}</span>
          </label>
          <Button
            variant="danger"
            className="font-semibold px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canConfirmDelete}
            onClick={() => setDelModalOpen(true)}
            data-testid="account-delete-btn"
          >
            <TrashSimple size={16} className="mr-2" />
            {t('profileSettings.account.delete.button')}
          </Button>
        </div>
      </div>

      {/* Block C — Inactivity policy (read-only) */}
      <div className="bg-surface_muted border border-border rounded-2xl p-6" data-testid="account-inactivity-block">
        <h2 className="font-semibold text-text_main mb-2 flex items-center gap-2">
          <Info size={20} />
          {t('profileSettings.account.inactivity.title')}
        </h2>
        <p className="text-sm text-text_muted">
          {t('profileSettings.account.inactivity.body')}
        </p>
      </div>

      {/* Modal — Deactivate confirmation */}
      <Dialog open={deactModalOpen} onOpenChange={setDeactModalOpen}>
        <DialogContent data-testid="deactivate-modal">
          <DialogHeader>
            <DialogTitle>{t('profileSettings.account.deactivate.modalTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text_muted">{t('profileSettings.account.deactivate.modalBody')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactModalOpen(false)} data-testid="deactivate-cancel">
              {t('profileSettings.account.deactivate.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={deactivating}
              onClick={doDeactivate}
              data-testid="deactivate-confirm"
            >
              {deactivating ? t('common.loading') : t('profileSettings.account.deactivate.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal — Delete final confirmation */}
      <Dialog open={delModalOpen} onOpenChange={setDelModalOpen}>
        <DialogContent data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle className="text-danger flex items-center gap-2">
              <WarningCircle size={22} weight="fill" />
              {t('profileSettings.account.delete.modalTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text_main">{t('profileSettings.account.delete.modalBody')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelModalOpen(false)} data-testid="delete-cancel">
              {t('profileSettings.account.delete.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={deleting}
              onClick={doDelete}
              data-testid="delete-confirm"
            >
              {deleting ? t('common.loading') : t('profileSettings.account.delete.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
